import NPCManager from '../../npc/NPCManager.js';
import NPCCreator from '../../ui/NPCCreator.js';
import Transform from '../../math/Transform.js';
import GroundEffect from '../../effects/GroundEffect.js';
import Debug from '../../utils/Debug.js';

/**
 * NPCTool - Editor UI for placing and managing NPCs.
 * Renders in the Game tab sidebar.
 */
export default class NPCTool {
  /**
   * @param {Object} options
   * @param {NPCManager} options.npcManager
   * @param {World} options.world
   * @param {Camera} options.camera
   * @param {HTMLElement} options.container - Sidebar content container
   * @param {ClickToMoveHandler} [options.clickToMoveHandler] - Disabled during placement
   */
  constructor(options = {}) {
    this.npcManager = options.npcManager;
    this.world = options.world;
    this.camera = options.camera;
    this.container = options.container;
    this.clickToMoveHandler = options.clickToMoveHandler || null;

    // Placement state
    this._placing = false;
    this._placingPatrol = false;
    this._patrolPoints = [];
    this._aggressive = false;
    this._waypointMarkers = [];

    // LPC NPC Creator
    this._npcCreator = null;
    this._npcCreatorOverlay = null;
    this._pendingLPCConfig = null; // Set when NPC Creator confirms, persists across placements
    this._lpcSelected = false; // True when dropdown is on "__lpc_custom__"

    // Click detection state (mousedown+mouseup, not click event — more reliable on CSS 3D elements)
    this._mouseDownPos = null;
    this._CLICK_THRESHOLD = 5;

    // Bound event handlers
    this._onMouseDownBound = this._onMouseDown.bind(this);
    this._onMouseUpBound = this._onMouseUp.bind(this);
    this._onTouchStartBound = this._onTouchStart.bind(this);
    this._onTouchEndBound = this._onTouchEnd.bind(this);
    this._onDblClickBound = this._onDblClick.bind(this);
    this._onKeyDownBound = this._onKeyDown.bind(this);

    this._buildUI();

    // Auto-enable placement when entering NPC submenu, cancel when leaving
    window.addEventListener('toolbar-submenu-change', (e) => {
      if (e.detail.level === 'npc') {
        if (!this._placing) {
          this._startPlacement();
        }
      } else if (this._placing) {
        this._cancelPlacement();
      }
    });
  }

  /**
   * Build the NPC management section in the sidebar
   * @private
   */
  _buildUI() {
    const section = document.createElement('div');
    section.id = 'npcSection';
    section.className = 'gui-section';

    // Hidden selects for internal state (toolbar controls these via public API)
    const characterNames = NPCManager.getCharacterNames();
    const characterOptions = characterNames.map(n =>
      `<option value="${n}">${n.charAt(0).toUpperCase() + n.slice(1)}</option>`
    ).join('');

    section.innerHTML = `
      <select id="npcCharacterSelect" style="display:none;">${characterOptions}</select>
      <select id="npcBehaviorSelect" style="display:none;">
        <option value="wander">Wander</option>
        <option value="patrol">Patrol</option>
      </select>
      <button id="npcPlaceBtn" style="display:none;"></button>
      <button id="npcDoneBtn" style="display:none;"></button>
      <button id="npcCancelBtn" style="display:none;"></button>

      <div id="npcNameRow" class="gui-row" style="margin-bottom: 8px;">
        <span class="gui-label" style="color: var(--text-secondary); font-size: 12px;">Name</span>
        <input type="text" id="npcNameInput" class="gui-input" style="flex: 1; font-size: 12px; padding: 3px 6px; color: var(--text-primary, #eee); background: var(--bg-secondary, #1a1a2e); border: 1px solid rgba(255,255,255,0.15); border-radius: 4px;" placeholder="(auto)" />
      </div>

      <div id="npcWanderSettings" class="gui-row" style="margin-bottom: 8px;">
        <span class="gui-label" style="color: var(--text-secondary); font-size: 12px;">Radius</span>
        <div class="gui-slider-row">
          <input type="range" id="npcWanderRadius" class="gui-slider" min="2" max="15" step="1" value="5">
          <input type="number" id="npcWanderRadiusValue" class="gui-input gui-slider-value" value="5" step="1" min="2" max="15">
        </div>
      </div>

      <div id="npcAggroSettings" class="gui-row" style="margin-bottom: 8px;">
        <span class="gui-label" style="color: var(--text-secondary); font-size: 12px;">Aggro</span>
        <div class="gui-slider-row">
          <input type="range" id="npcAggroRange" class="gui-slider" min="2" max="20" step="1" value="8">
          <input type="number" id="npcAggroRangeValue" class="gui-input gui-slider-value" value="8" step="1" min="2" max="20">
        </div>
      </div>

      <div id="npcPatrolInfo" style="display:none; padding: 4px 0; font-size: 11px; color: var(--text-secondary);">
        Click surfaces to add patrol waypoints. Double-click or press Done to finish.
      </div>

      <div id="npcStatusText" style="padding: 4px 0; font-size: 11px; color: var(--text-secondary);"></div>

      <div id="npcList"></div>
    `;

    this.container.appendChild(section);

    // Cache elements
    this._charSelect = document.getElementById('npcCharacterSelect');
    this._behaviorSelect = document.getElementById('npcBehaviorSelect');
    this._nameInput = document.getElementById('npcNameInput');
    this._wanderSettings = document.getElementById('npcWanderSettings');
    this._aggroSettings = document.getElementById('npcAggroSettings');
    this._aggroSlider = document.getElementById('npcAggroRange');
    this._aggroValue = document.getElementById('npcAggroRangeValue');
    this._patrolInfo = document.getElementById('npcPatrolInfo');
    this._radiusSlider = document.getElementById('npcWanderRadius');
    this._radiusValue = document.getElementById('npcWanderRadiusValue');
    this._placeBtn = document.getElementById('npcPlaceBtn');
    this._doneBtn = document.getElementById('npcDoneBtn');
    this._cancelBtn = document.getElementById('npcCancelBtn');
    this._statusText = document.getElementById('npcStatusText');
    this._listEl = document.getElementById('npcList');

    // Event listeners
    this._behaviorSelect.addEventListener('change', () => this._onBehaviorChange());
    this._radiusSlider.addEventListener('input', (e) => {
      this._radiusValue.value = e.target.value;
    });
    this._radiusValue.addEventListener('input', (e) => {
      this._radiusSlider.value = e.target.value;
    });
    this._aggroSlider.addEventListener('input', (e) => {
      this._aggroValue.value = e.target.value;
    });
    this._aggroValue.addEventListener('input', (e) => {
      this._aggroSlider.value = e.target.value;
    });
    this._doneBtn.addEventListener('click', () => this._finishPatrol());

    this._onBehaviorChange();
    this.refreshList();
  }

  /**
   * Show/hide wander vs patrol settings
   * @private
   */
  _onBehaviorChange() {
    const isPatrol = this._behaviorSelect.value === 'patrol';
    this._wanderSettings.style.display = isPatrol ? 'none' : '';
    this._aggroSettings.style.display = this._aggressive ? '' : 'none';
    this._patrolInfo.style.display = 'none'; // Only show during placement
  }

  /**
   * Start NPC placement mode
   * @private
   */
  _startPlacement() {
    const behavior = this._behaviorSelect.value;

    if (behavior === 'patrol') {
      this._placing = true;
      this._placingPatrol = true;
      this._patrolPoints = [];
      this._clearWaypointMarkers();
      this._patrolInfo.style.display = '';
      this._doneBtn.style.display = '';
      this._cancelBtn.style.display = '';
      this._placeBtn.style.display = 'none';
      this._statusText.textContent = 'Click a surface to set spawn point...';
    } else {
      this._placing = true;
      this._placingPatrol = false;
      this._cancelBtn.style.display = '';
      this._placeBtn.style.display = 'none';
      this._statusText.textContent = 'Click a surface to place NPC...';
    }

    // Listen for mousedown/mouseup (more reliable than click on CSS 3D elements)
    const containerEl = document.getElementById('container');
    if (containerEl) {
      containerEl.addEventListener('mousedown', this._onMouseDownBound, true);
      containerEl.addEventListener('mouseup', this._onMouseUpBound, true);
      containerEl.addEventListener('touchstart', this._onTouchStartBound, { capture: true, passive: false });
      containerEl.addEventListener('touchend', this._onTouchEndBound, { capture: true, passive: false });
    }
    document.addEventListener('dblclick', this._onDblClickBound, true);
    document.addEventListener('keydown', this._onKeyDownBound);

    this._dispatchPatrolUpdate();
  }

  /**
   * Cancel placement mode
   * @private
   */
  _cancelPlacement() {
    const wasPlacing = this._placing || this._placingPatrol;

    this._clearWaypointMarkers();
    this._placing = false;
    this._placingPatrol = false;
    this._patrolPoints = [];

    this._placeBtn.style.display = '';
    this._doneBtn.style.display = 'none';
    this._cancelBtn.style.display = 'none';
    this._patrolInfo.style.display = 'none';
    this._statusText.textContent = '';

    const containerEl = document.getElementById('container');
    if (containerEl) {
      containerEl.removeEventListener('mousedown', this._onMouseDownBound, true);
      containerEl.removeEventListener('mouseup', this._onMouseUpBound, true);
      containerEl.removeEventListener('touchstart', this._onTouchStartBound, true);
      containerEl.removeEventListener('touchend', this._onTouchEndBound, true);
    }
    this._mouseDownPos = null;
    document.removeEventListener('dblclick', this._onDblClickBound, true);
    document.removeEventListener('keydown', this._onKeyDownBound);

    this._dispatchPatrolUpdate();
    // Click-to-move is managed by setupModeSwitching at the mode level
  }

  /**
   * Handle keyboard during placement
   * @private
   */
  _onKeyDown(event) {
    if (event.key === 'Escape') {
      this._cancelPlacement();
    }
  }

  /**
   * Handle double-click (finish patrol placement)
   * @private
   */
  _onDblClick(event) {
    if (this._placingPatrol && this._patrolPoints.length >= 2) {
      event.preventDefault();
      event.stopPropagation();
      this._finishPatrol();
    }
  }

  /**
   * Handle mousedown during placement — record position
   * @private
   */
  _onMouseDown(event) {
    if (!this._placing) return;
    if (event.button !== 0) return;
    this._mouseDownPos = { x: event.clientX, y: event.clientY };
  }

  /**
   * Handle mouseup during placement — check distance and process click
   * @private
   */
  _onMouseUp(event) {
    if (!this._placing) return;
    if (event.button !== 0) return;
    if (!this._mouseDownPos) return;

    const dx = event.clientX - this._mouseDownPos.x;
    const dy = event.clientY - this._mouseDownPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    this._mouseDownPos = null;

    if (distance > this._CLICK_THRESHOLD) return;

    this._handlePlacementClick(event.clientX, event.clientY, event.target);
  }

  /**
   * Handle touchstart during placement
   * @private
   */
  _onTouchStart(event) {
    if (!this._placing) return;
    if (event.touches.length !== 1) return;
    const touch = event.touches[0];
    this._mouseDownPos = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * Handle touchend during placement
   * @private
   */
  _onTouchEnd(event) {
    if (!this._placing) return;
    if (!this._mouseDownPos) return;
    if (event.changedTouches.length === 0) {
      this._mouseDownPos = null;
      return;
    }

    const touch = event.changedTouches[0];
    const dx = touch.clientX - this._mouseDownPos.x;
    const dy = touch.clientY - this._mouseDownPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    this._mouseDownPos = null;

    if (distance > this._CLICK_THRESHOLD) return;

    // Only preventDefault on placement surfaces — UI elements (toolbar, panels)
    // need the synthetic click event to function on mobile
    const target = event.target;
    const isUI = target.closest && (target.closest('.top-toolbar') || target.closest('.top-sheet') || target.closest('.settings-panel') || target.closest('.info-panel'));
    if (!isUI) {
      event.preventDefault();
    }

    this._handlePlacementClick(touch.clientX, touch.clientY, target);
  }

  /**
   * Process a placement click at the given screen coordinates
   * @private
   */
  _handlePlacementClick(clientX, clientY, target) {
    // Find clicked face
    const faceEl = target.closest ? target.closest('.face') : null;
    if (!faceEl) return;

    // Skip UI clicks
    if (target.closest('.top-toolbar') || target.closest('.top-sheet') || target.closest('.settings-panel') || target.closest('.info-panel')) return;

    const cubeEl = faceEl.closest('.cube');
    if (!cubeEl) return;

    const cuboid = this.world.findCuboidByElement(cubeEl);
    if (!cuboid) return;

    // Use effective cuboid for fragments
    const effectiveCuboid = cuboid.isFragment
      ? this.world.cuboids.get(cuboid.parentKey) || cuboid
      : cuboid;

    // Resolve world position — accept any face, place NPC on top of clicked cuboid
    const pos = this._resolveClickPosition(effectiveCuboid, clientX, clientY);
    if (!pos) return;

    if (this._placingPatrol) {
      // Add patrol waypoint
      this._patrolPoints.push(pos);
      this._addWaypointMarker(pos, this._patrolPoints.length);
      this._statusText.textContent = `Patrol points: ${this._patrolPoints.length}. Click more or press Done.`;
      Debug.log('NPCTool', `Added patrol point ${this._patrolPoints.length}: (${pos.x.toFixed(1)}, ${pos.y.toFixed(1)}, ${pos.z.toFixed(1)})`);
      this._dispatchPatrolUpdate();
    } else {
      // Wander placement: place immediately
      this._placeWanderNPC(pos);
    }
  }

  /**
   * Finish patrol placement — spawn NPC with collected waypoints
   * @private
   */
  async _finishPatrol() {
    if (this._patrolPoints.length < 2) {
      this._statusText.textContent = 'Need at least 2 patrol points.';
      return;
    }

    const spawnPos = this._patrolPoints[0];
    const isLPC = this._lpcSelected && this._pendingLPCConfig;

    try {
      this._statusText.textContent = 'Spawning NPC...';
      let spawnConfig;

      if (isLPC) {
        spawnConfig = {
          ...this._pendingLPCConfig,
          x: spawnPos.x,
          y: spawnPos.y,
          z: spawnPos.z,
          behaviorType: 'patrol',
          patrolPoints: this._patrolPoints.map(p => ({ x: p.x, y: p.y, z: p.z }))
        };
        // Apply aggressive toggle
        if (this._aggressive && !spawnConfig.combatConfig) {
          spawnConfig.combatConfig = {
            isHostile: true,
            aggroRange: this.getAggroRange(),
            deaggroRange: this.getAggroRange() + 6,
            attackRange: 1.8,
            attackCooldown: 1.0
          };
        } else if (!this._aggressive && spawnConfig.combatConfig) {
          delete spawnConfig.combatConfig;
        }
      } else {
        const characterName = this._charSelect.value;
        spawnConfig = {
          characterName,
          x: spawnPos.x,
          y: spawnPos.y,
          z: spawnPos.z,
          behaviorType: 'patrol',
          patrolPoints: this._patrolPoints.map(p => ({ x: p.x, y: p.y, z: p.z }))
        };
        if (this._aggressive) {
          spawnConfig.combatConfig = {
            isHostile: true,
            aggroRange: this.getAggroRange(),
            deaggroRange: this.getAggroRange() + 6,
            attackRange: 1.8,
            attackCooldown: 1.0
          };
        }
      }
      // Apply custom name from toolbar
      const customName = this.getNPCName();
      if (customName) spawnConfig.displayName = customName;

      const character = await this.npcManager.spawnNPC(spawnConfig);
      const displayName = spawnConfig.displayName || spawnConfig.characterName || 'LPC NPC';
      this._statusText.textContent = `Placed ${displayName}${this._aggressive ? ' (hostile)' : ''} (patrol) — click to start another`;

      // Record undo action
      if (this.undoManager && character) {
        const npcId = character.id;
        const config = { ...spawnConfig, id: npcId };
        const npcManager = this.npcManager;
        const tool = this;
        this.undoManager.push({
          description: 'Place patrol NPC',
          undo: () => { npcManager.removeNPC(npcId); tool.refreshList(); },
          redo: async () => { await npcManager.spawnNPC(config); tool.refreshList(); }
        });
      }
    } catch (error) {
      this._statusText.textContent = `Error: ${error.message}`;
      Debug.error('NPCTool', 'Failed to spawn patrol NPC:', error);
    }

    // Reset patrol state but stay in placement mode for multi-place
    this._clearWaypointMarkers();
    this._patrolPoints = [];
    this._placingPatrol = true;
    this._patrolInfo.style.display = '';
    this._statusText.textContent = 'Click a surface to set spawn point...';
    this.refreshList();
    this._dispatchPatrolUpdate();
  }

  /**
   * Place a wander NPC at the clicked position
   * @private
   */
  async _placeWanderNPC(pos) {
    const wanderRadius = parseInt(this._radiusSlider.value) || 5;

    try {
      this._statusText.textContent = 'Spawning NPC...';
      let spawnConfig;

      if (this._lpcSelected && this._pendingLPCConfig) {
        // LPC NPC from creator — merge with current behavior settings
        spawnConfig = {
          ...this._pendingLPCConfig,
          x: pos.x,
          y: pos.y,
          z: pos.z,
          behaviorType: 'wander',
          wanderRadius
        };
        // Apply aggressive toggle — add or remove combatConfig
        if (this._aggressive && !spawnConfig.combatConfig) {
          spawnConfig.combatConfig = {
            isHostile: true,
            aggroRange: this.getAggroRange(),
            deaggroRange: this.getAggroRange() + 6,
            attackRange: 1.8,
            attackCooldown: 1.0
          };
        } else if (!this._aggressive && spawnConfig.combatConfig) {
          delete spawnConfig.combatConfig;
        }
      } else {
        // Legacy NPC
        const characterName = this._charSelect.value;
        spawnConfig = {
          characterName,
          x: pos.x,
          y: pos.y,
          z: pos.z,
          behaviorType: 'wander',
          wanderRadius
        };
        if (this._aggressive) {
          spawnConfig.combatConfig = {
            isHostile: true,
            aggroRange: this.getAggroRange(),
            deaggroRange: this.getAggroRange() + 6,
            attackRange: 1.8,
            attackCooldown: 1.0
          };
        }
      }
      // Apply custom name from toolbar
      const customName = this.getNPCName();
      if (customName) spawnConfig.displayName = customName;

      const character = await this.npcManager.spawnNPC(spawnConfig);
      const displayName = spawnConfig.displayName || spawnConfig.characterName || 'LPC NPC';
      this._statusText.textContent = `Placed ${displayName} — click to place another`;

      // Record undo action
      if (this.undoManager && character) {
        const npcId = character.id;
        const config = { ...spawnConfig, id: npcId };
        const npcManager = this.npcManager;
        const tool = this;
        this.undoManager.push({
          description: 'Place NPC',
          undo: () => { npcManager.removeNPC(npcId); tool.refreshList(); },
          redo: async () => { await npcManager.spawnNPC(config); tool.refreshList(); }
        });
      }
    } catch (error) {
      this._statusText.textContent = `Error: ${error.message}`;
      Debug.error('NPCTool', 'Failed to spawn wander NPC:', error);
    }

    // Stay in placement mode for multi-place (don't cancel)
    this.refreshList();
  }

  /**
   * Resolve click to a placement position on top of a cuboid.
   * Accepts any face click — always places on the cuboid's top surface.
   * For noCollision cuboids (water/gas), finds a walkable surface below.
   * @private
   */
  _resolveClickPosition(cuboid, clientX, clientY) {
    const CUBE_SIZE = this.camera.CUBE_SIZE;
    const screenX = clientX - window.innerWidth / 2;
    const screenY = clientY - window.innerHeight / 2;
    const renderingYOffset = CUBE_SIZE / 2;
    const adjustedScreenY = screenY - renderingYOffset * this.camera.zoomLevel;

    const topY = cuboid.y + cuboid.height;
    const faceWorldY = topY * CUBE_SIZE;
    const worldPos = Transform.screenToWorld(screenX, adjustedScreenY, this.camera, faceWorldY);

    const worldX = worldPos.x / CUBE_SIZE;
    const worldZ = -worldPos.z / CUBE_SIZE;

    // For noCollision cuboids (water, gas), find walkable surface below
    if (cuboid.hasNoCollision && cuboid.hasNoCollision()) {
      return this._findWalkableSurfaceBelow(worldX, topY, worldZ);
    }

    // Clamp to cuboid bounds with margin
    const margin = 0.3;
    const x = Math.max(cuboid.x + margin, Math.min(cuboid.x + cuboid.width - margin, worldX));
    const z = Math.max(cuboid.z + margin, Math.min(cuboid.z + cuboid.depth - margin, worldZ));

    // Validate against nav mesh — reject if top surface isn't walkable
    const navGraph = this.clickToMoveHandler && this.clickToMoveHandler.navGraph;
    if (navGraph) {
      const node = navGraph.findNodeAtPoint(x, topY, z);
      if (!node) {
        return null;
      }
    }

    return { x, y: topY, z };
  }

  /**
   * Find a walkable surface below a given position (for noCollision cuboids).
   * @private
   */
  _findWalkableSurfaceBelow(x, y, z) {
    const navGraph = this.clickToMoveHandler && this.clickToMoveHandler.navGraph;
    if (!navGraph) return null;

    let bestNode = null;
    let bestY = -Infinity;

    for (const node of navGraph.getAllNodes()) {
      if (!node.containsPoint(x, z)) continue;
      if (node.y > y) continue;
      if (node.y > bestY) {
        bestY = node.y;
        bestNode = node;
      }
    }

    if (bestNode) {
      const margin = 0.3;
      const clampedX = Math.max(bestNode.minX + margin, Math.min(bestNode.maxX - margin, x));
      const clampedZ = Math.max(bestNode.minZ + margin, Math.min(bestNode.maxZ - margin, z));
      return { x: clampedX, y: bestNode.y, z: clampedZ };
    }

    return null;
  }

  /**
   * Refresh the NPC list in the sidebar
   */
  refreshList() {
    const npcs = this.npcManager.getNPCList();
    if (npcs.length === 0) {
      this._listEl.innerHTML = '<div style="font-size: 11px; color: var(--text-secondary); padding: 4px 0;">No NPCs placed</div>';
      return;
    }

    let html = '';
    for (const npc of npcs) {
      const name = npc.displayName
        || npc.characterName.charAt(0).toUpperCase() + npc.characterName.slice(1);
      const badge = npc.behaviorType === 'patrol' ? 'P' : 'W';
      const lpcBadge = npc.isLPC ? ' <span style="color: rgba(100,200,255,0.8); font-size: 9px;">LPC</span>' : '';
      const hostileBadge = npc.isHostile ? ' <span style="color: rgba(255,80,80,0.9); font-size: 10px; font-weight: 700;">!!</span>' : '';
      html += `
        <div style="display: flex; align-items: center; justify-content: space-between; padding: 4px 0; font-size: 11px; border-bottom: 1px solid rgba(255,255,255,0.05);">
          <span style="color: var(--text-primary);">${name} <span style="color: var(--text-secondary); font-size: 10px;">${badge}</span>${lpcBadge}${hostileBadge}</span>
          <button class="npc-remove-btn" data-npc-id="${npc.id}" style="padding: 2px 8px; font-size: 10px; background: transparent; border: 1px solid rgba(255,80,80,0.4); color: rgba(255,80,80,0.8); border-radius: 4px; cursor: pointer;">✕</button>
        </div>
      `;
    }
    this._listEl.innerHTML = html;

    // Attach remove handlers
    this._listEl.querySelectorAll('.npc-remove-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = e.target.dataset.npcId;

        // Capture NPC config for undo before removal
        const npcEntry = this.npcManager.npcs.get(id);
        let savedConfig = null;
        if (npcEntry && this.undoManager) {
          const { character, behavior } = npcEntry;
          savedConfig = {
            id,
            characterName: character.characterName,
            x: behavior.spawnPoint.x,
            y: behavior.spawnPoint.y,
            z: behavior.spawnPoint.z,
            behaviorType: behavior.type,
            wanderRadius: behavior.wanderRadius,
            patrolPoints: behavior.patrolPoints.map(p => ({ x: p.x, y: p.y, z: p.z }))
          };
          if (character.isLPC) {
            savedConfig.lpcAppearance = character.lpcAppearance;
            savedConfig.lpcEquipment = character.lpcEquipment;
            savedConfig.displayName = character.displayName;
          }
          if (behavior.combatConfig && behavior.combatConfig.isHostile) {
            savedConfig.combatConfig = { ...behavior.combatConfig };
          }
        }

        this.npcManager.removeNPC(id);
        this.refreshList();

        if (this.undoManager && savedConfig) {
          const npcManager = this.npcManager;
          const tool = this;
          this.undoManager.push({
            description: 'Remove NPC',
            undo: async () => { await npcManager.spawnNPC(savedConfig); tool.refreshList(); },
            redo: () => { npcManager.removeNPC(id); tool.refreshList(); }
          });
        }
      });
    });
  }

  // ---- Waypoint Markers ----

  /**
   * Add a visual marker at a patrol waypoint position
   * @param {{x,y,z}} pos - World position
   * @param {number} index - Waypoint number (1-based)
   * @private
   */
  _addWaypointMarker(pos, index) {
    const marker = new GroundEffect({
      x: pos.x,
      y: pos.y,
      z: pos.z,
      radius: 0.4,
      color: { r: 220, g: 60, b: 60 },
      opacity: 0.85,
      animation: 'pulse',
      cubeSize: this.camera.CUBE_SIZE,
      label: String(index)
    });
    this.world.addGroundEffect(marker);
    marker.show();
    this._waypointMarkers.push(marker);
  }

  /**
   * Remove all waypoint markers from the world
   * @private
   */
  _clearWaypointMarkers() {
    for (const marker of this._waypointMarkers) {
      this.world.removeGroundEffect(marker.id);
    }
    this._waypointMarkers = [];
  }

  // ── LPC NPC Creator ────────────────────────────────────────────────

  /**
   * Open the LPC NPC Creator overlay.
   * On confirm, sets _pendingLPCConfig and enters placement mode.
   */
  openLPCCreator() {
    if (this._npcCreatorOverlay) return; // Already open

    const overlay = document.createElement('div');
    overlay.className = 'npc-creator-overlay';
    overlay.style.cssText = `
      position: fixed; inset: 0; z-index: 10000;
      background: rgba(0,0,0,0.7);
      display: flex; align-items: center; justify-content: center;
    `;

    const panel = document.createElement('div');
    panel.style.cssText = `
      width: 400px; max-height: 90vh; overflow-y: auto;
      background: var(--bg-primary, #1a1a2e);
      border-radius: 12px; padding: 16px;
      border: 1px solid rgba(255,255,255,0.1);
    `;

    const creator = new NPCCreator({
      onConfirm: (npcConfig) => {
        this._pendingLPCConfig = npcConfig;
        this._lpcSelected = true;
        this.closeLPCCreator();
        // Update char select to show LPC Custom
        this._ensureLPCOption();
        this._charSelect.value = '__lpc_custom__';
        // Notify toolbar to re-render with LPC Custom selected
        window.dispatchEvent(new CustomEvent('npc-lpc-config-changed'));
        // Start placement mode with the LPC config
        if (!this._placing) {
          this._startPlacement();
        }
        this._statusText.textContent = `Click a surface to place ${npcConfig.displayName || 'LPC NPC'}...`;
      },
      onCancel: () => {
        this.closeLPCCreator();
      }
    });

    panel.appendChild(creator.buildContent());

    // Restore previous selection if re-opening
    if (this._pendingLPCConfig) {
      creator.loadConfig(this._pendingLPCConfig);
    }

    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    this._npcCreator = creator;
    this._npcCreatorOverlay = overlay;

    // Close on overlay background click
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.closeLPCCreator();
    });
  }

  /**
   * Close the LPC NPC Creator overlay.
   */
  closeLPCCreator() {
    if (this._npcCreator) {
      this._npcCreator.destroy();
      this._npcCreator = null;
    }
    if (this._npcCreatorOverlay) {
      this._npcCreatorOverlay.remove();
      this._npcCreatorOverlay = null;
    }
  }

  /** @returns {boolean} Whether LPC Custom is currently selected */
  isLPCSelected() {
    return this._lpcSelected && !!this._pendingLPCConfig;
  }

  /** @returns {string|null} Display name of pending LPC config */
  getLPCDisplayName() {
    return this._pendingLPCConfig?.displayName || null;
  }

  /**
   * Ensure the "__lpc_custom__" option exists in the character select.
   * @private
   */
  _ensureLPCOption() {
    let opt = this._charSelect.querySelector('option[value="__lpc_custom__"]');
    if (!opt) {
      opt = document.createElement('option');
      opt.value = '__lpc_custom__';
      this._charSelect.insertBefore(opt, this._charSelect.firstChild);
    }
    const name = this._pendingLPCConfig?.displayName || 'LPC Custom';
    opt.textContent = `✦ ${name}`;
  }

  // ── Public API for TopToolbar ──────────────────────────────────────

  /** @returns {string[]} Available character names */
  getCharacterNames() {
    return NPCManager.getCharacterNames();
  }

  /** @returns {string} Currently selected character */
  getCharacter() {
    return this._charSelect.value;
  }

  /** Set the selected character */
  setCharacter(name) {
    if (name === '__lpc_custom__') {
      this._lpcSelected = true;
      this._ensureLPCOption();
    } else {
      this._lpcSelected = false;
    }
    this._charSelect.value = name;
  }

  /** @returns {string} 'wander' or 'patrol' */
  getBehavior() {
    return this._behaviorSelect.value;
  }

  /** Set the behavior mode — restarts placement if currently placing */
  setBehavior(mode) {
    this._behaviorSelect.value = mode;
    this._onBehaviorChange();
    // Restart placement with new behavior if already placing
    if (this._placing) {
      this._cancelPlacement();
      this._startPlacement();
    }
  }

  /** @returns {boolean} Whether currently in placement mode */
  isPlacing() {
    return this._placing;
  }

  /** @returns {number} Number of patrol waypoints collected */
  getPatrolPointCount() {
    return this._patrolPoints.length;
  }

  /** Public wrapper to finish patrol and spawn NPC */
  finishPatrol() {
    this._finishPatrol();
  }

  /** @returns {number} Aggro range from slider */
  getAggroRange() {
    return parseInt(this._aggroSlider?.value) || 8;
  }

  /** @returns {string} Custom NPC name (empty string = auto) */
  getNPCName() {
    return this._nameInput ? this._nameInput.value.trim() : '';
  }

  /** Set the NPC name input */
  setNPCName(name) {
    if (this._nameInput) this._nameInput.value = name || '';
  }

  /** @returns {boolean} Whether newly placed NPCs will be hostile */
  getAggressive() {
    return this._aggressive;
  }

  /** Set the aggressive flag for newly placed NPCs */
  setAggressive(isAggressive) {
    this._aggressive = isAggressive;
    this._aggroSettings.style.display = isAggressive ? '' : 'none';
  }

  /** Dispatch patrol state update so toolbar can reflect changes */
  _dispatchPatrolUpdate() {
    window.dispatchEvent(new CustomEvent('npc-patrol-update', {
      detail: {
        placing: this._placing,
        patrol: this._placingPatrol,
        pointCount: this._patrolPoints.length
      }
    }));
  }
}
